// Audit viewer (#/audit; supervisor, admin, auditor): filters, the audit table, and "Verify chain" for one day.

import { Fragment, useState, type FormEvent } from 'react';
import { api, type AuditQuery, type AuditVerify } from '../api.ts';
import { Icon } from '../components/Icon.tsx';
import { Card, Empty, ErrorBox, InlineError, JsonBlock, Loading } from '../components/ui.tsx';
import { actorCls, actorName } from '../console/util.ts';
import { useAction, useLoad } from '../hooks.ts';
import { fmtLocal, OPERATOR_TZ } from '../time.ts';

const EMPTY: Required<Omit<AuditQuery, 'limit'>> = { subjectType: '', subjectId: '', siteId: '', actorId: '', action: '', from: '', to: '' };

function localToIso(v: string): string | undefined {
  if (!v) return undefined;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

function today(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function AuditScreen() {
  const [draft, setDraft] = useState(EMPTY);
  const [limit, setLimit] = useState(200);
  const [q, setQ] = useState<AuditQuery>({ limit: 200 });
  const load = useLoad((signal) => api.audit(q, signal), [q]);
  const [open, setOpen] = useState<string | null>(null);
  const [day, setDay] = useState(today());
  const [verified, setVerified] = useState<{ day: string; r: AuditVerify } | null>(null);
  const verify = useAction(api.auditVerify);

  const apply = (e?: FormEvent) => {
    e?.preventDefault();
    setQ({
      subjectType: draft.subjectType.trim() || undefined,
      subjectId: draft.subjectId.trim() || undefined,
      siteId: draft.siteId.trim() || undefined,
      actorId: draft.actorId.trim() || undefined,
      action: draft.action.trim() || undefined,
      from: localToIso(draft.from),
      to: localToIso(draft.to),
      limit,
    });
  };
  const set = (k: keyof typeof EMPTY) => (v: string) => setDraft((d) => ({ ...d, [k]: v }));
  const rows = load.data ?? [];

  return (
    <div className="page">
      <div className="page-h">
        <div>
          <h2>Audit</h2>
          <p>Append-only record of who did what. Times in your zone ({OPERATOR_TZ}).</p>
        </div>
      </div>

      <Card
        title={
          <>
            <Icon name="shield" />
            Verify chain
          </>
        }
      >
        <div className="filters">
          <label className="field inline">
            <span className="lbl">Day (UTC)</span>
            <input type="date" value={day} onChange={(e) => setDay(e.target.value)} />
          </label>
          <div className="field end">
            <button
              type="button"
              className="btn"
              disabled={!day || verify.busy}
              onClick={() =>
                void verify.run(day).then((r) => {
                  if (r) setVerified({ day, r });
                })
              }
            >
              <Icon name="check" />
              {verify.busy ? 'Verifying…' : 'Verify chain'}
            </button>
          </div>
          {verified && !verify.busy && (
            <div className={`banner ${verified.r.ok ? 'ok' : 'warnish'}`} role="status">
              <Icon name={verified.r.ok ? 'check' : 'alert'} />
              <span>
                {verified.day}: {verified.r.ok ? 'chain intact' : 'chain broken'} · {verified.r.rows} rows checked
                {!verified.r.ok && verified.r.brokenAt ? ` · first break at ${verified.r.brokenAt}` : ''}
              </span>
            </div>
          )}
          <InlineError error={verify.error} />
        </div>
      </Card>

      <form className="filters" onSubmit={apply}>
        <label className="field inline">
          <span className="lbl">Subject type</span>
          <input value={draft.subjectType} onChange={(e) => set('subjectType')(e.target.value)} placeholder="event, site, contact…" />
        </label>
        <label className="field inline">
          <span className="lbl">Subject ID</span>
          <input value={draft.subjectId} onChange={(e) => set('subjectId')(e.target.value)} className="mono" />
        </label>
        <label className="field inline">
          <span className="lbl">Site ID</span>
          <input value={draft.siteId} onChange={(e) => set('siteId')(e.target.value)} className="mono" />
        </label>
        <label className="field inline">
          <span className="lbl">Actor ID</span>
          <input value={draft.actorId} onChange={(e) => set('actorId')(e.target.value)} className="mono" />
        </label>
        <label className="field inline">
          <span className="lbl">Action</span>
          <input value={draft.action} onChange={(e) => set('action')(e.target.value)} placeholder="event.claim, video.access…" />
        </label>
        <label className="field inline">
          <span className="lbl">From</span>
          <input type="datetime-local" value={draft.from} onChange={(e) => set('from')(e.target.value)} />
        </label>
        <label className="field inline">
          <span className="lbl">To</span>
          <input type="datetime-local" value={draft.to} onChange={(e) => set('to')(e.target.value)} />
        </label>
        <label className="field">
          <span className="lbl">Limit</span>
          <select value={limit} onChange={(e) => setLimit(Number(e.target.value))} className="compact">
            {[100, 200, 500, 1000].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <div className="row-wrap">
          <button type="submit" className="btn pri">
            <Icon name="search" />
            Apply
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => {
              setDraft(EMPTY);
              setQ({ limit });
            }}
          >
            Clear
          </button>
        </div>
      </form>

      <div className="card2 flush">
        {load.error && !load.data ? (
          <ErrorBox error={load.error} onRetry={load.reload} title="Could not load the audit log" />
        ) : !load.data ? (
          <Loading label="Loading audit rows…" />
        ) : rows.length === 0 ? (
          <Empty>No audit rows match.</Empty>
        ) : (
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">Actor</th>
                  <th scope="col">Action</th>
                  <th scope="col">Subject</th>
                  <th scope="col">Site</th>
                  <th scope="col">Data</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <Fragment key={r.id}>
                    <tr>
                      <td className="mono nowrap">{fmtLocal(r.at)}</td>
                      <td>
                        <span className={actorCls(r.actor)}>{actorName(r.actor)}</span>
                        <div className="note mono">{r.actor.type}</div>
                      </td>
                      <td className="mono">{r.action}</td>
                      <td>
                        <span className="mono">{r.subjectType}</span>
                        <div className="note mono" title={r.subjectId}>
                          {r.subjectId}
                        </div>
                      </td>
                      <td className="mono small">{r.siteId ?? '—'}</td>
                      <td>
                        {r.data === null || (typeof r.data === 'object' && !Array.isArray(r.data) && Object.keys(r.data).length === 0) ? (
                          <span className="dim">—</span>
                        ) : (
                          <button type="button" className="btn sm" aria-expanded={open === r.id} onClick={() => setOpen((o) => (o === r.id ? null : r.id))}>
                            {open === r.id ? 'Hide' : 'Show'}
                          </button>
                        )}
                      </td>
                    </tr>
                    {open === r.id && (
                      <tr className="subrow">
                        <td colSpan={6}>
                          <JsonBlock value={r.data} maxHeight={320} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {load.data && (
          <div className="tfoot note">
            {rows.length} rows{rows.length >= (q.limit ?? 200) ? ` · limit reached; narrow the filters or raise the limit` : ''}
          </div>
        )}
      </div>
    </div>
  );
}
